/**
 * What deleting a vault means to bill for and to hope for, per destination —
 * the retention and recoverability sentences the desktop app's Delete
 * dialog will show beside its warning (the dialog itself is a later ticket),
 * and the anchor of the Help guide section that walks through doing the same
 * thing by hand on that provider.
 *
 * Every sentence here describes **the app's own Delete**, not a delete the
 * user performs at the provider's console. The two differ, and on three
 * providers they differ in the direction that costs money: the app's Drive
 * delete bypasses Trash, a folder dragged to the bin by hand does not; the
 * app deletes S3 objects by version id, a console delete by name leaves the
 * versions underneath. Anything rendering these sentences beside a
 * by-hand guide has to say which of the two it is describing — see
 * `apps/portal/src/help/DeleteVaultProviderNoteTable.tsx`.
 *
 * `"s3"` covers every object-store destination, from Backblaze B2 to a
 * self-hosted MinIO and Azure Blob Storage, and the honest answer differs by
 * provider — Backblaze has no minimum retention, Wasabi bills the remainder of
 * a 90-day one, Azure may keep what was deleted under soft delete, and one the
 * app doesn't recognise might have anything. So for `"s3"` the note is also
 * keyed by the vault's recorded provider (#411, #611): sniffed once from the
 * endpoint host for a bucket the app reaches over S3, and recorded as `azure`
 * by the Azure vault form itself, which has no host to sniff (ADR 0023). This
 * file never looks at a host.
 *
 * One table, read by the dialog and rendered as a page by
 * `apps/portal/src/help/guides/delete-a-vault-by-hand.md`'s table, so app and
 * site answer out of the same sentences rather than two sets that drift.
 *
 * One seam, deliberately cut. Drive's row also varies by `scope` (below, #224)
 * and a page has no vault in front of it to have one, so the site renders that
 * row's *attached* sentence while the app renders whichever of the two the
 * vault in front of it earns. Every other row is the same on both. The seam is
 * pinned from both sides — `vaultDeleteProviderNote.test.ts` for the fallback,
 * `HelpGuide.test.tsx` for what the page ends up showing — and the page says
 * in its own words which of the two it is showing, so a reader does not
 * discover the other half by opening the app on a vault. Any new row that wants
 * to vary has to widen this comment first.
 *
 * **Second dimension, added by #413: the vault's own storage class.** Every
 * `"s3"` row above answers for the *provider*, with no idea which of its
 * classes the vault's contents are actually kept in — the
 * `"Standard-class objects delete free; other storage classes may bill for
 * early deletion"` hedge exists because AWS and GCS each have several, and the
 * provider alone cannot tell them apart (Azure's row hedges the same way over
 * its tiers). A vault now records its own provider
 * and class (#411, ADR 0007), so a caller that has one no longer has to hedge:
 * `classRetentionSentence` looks the pair up in the shared catalogue
 * (`storageClasses.ts`) and, for a class with a `retentionFloorDays`, states
 * that floor by the class's own name — the way the Wasabi row already did,
 * generalised to every provider instead of hand-written once for the one
 * provider with a single class. `vaultDeleteProviderNote`'s third argument is
 * this pair, and it is also what names the provider; omitting it, or handing it a class with no floor, or a provider
 * the catalogue has no rows for (`"other"`), leaves the provider's own sentence
 * above exactly as it reads today — `vaultDeleteProviderNote.test.ts` pins
 * both directions. This reopens what #161 closed as wontfix while the app
 * wrote only standard-class objects (ADR 0009's consequences).
 */

import { lookupStorageClass } from "./storageClasses";
import type { VaultProvider, VaultStorageTier } from "./storageTier";
import { vaultProviderName } from "./storageTier";
import type { DestinationKind } from "./types";

/**
 * The Help guide section a note's `guideAnchor` points at, by the anchor
 * `github-slugger` gives that section's heading in
 * `delete-a-vault-by-hand.md` — `HelpGuide.test.tsx` pins these same seven
 * strings against the rendered page, so a heading reworded there without
 * updating here fails loudly instead of quietly 404ing an in-page link.
 */
export const DELETE_VAULT_GUIDE_ANCHOR_DRIVE = "a-drive-or-network-share";
export const DELETE_VAULT_GUIDE_ANCHOR_BACKBLAZE = "backblaze-b2";
export const DELETE_VAULT_GUIDE_ANCHOR_S3_COMPATIBLE = "another-s3-compatible-provider";
export const DELETE_VAULT_GUIDE_ANCHOR_AZURE = "azure-blob-storage";
export const DELETE_VAULT_GUIDE_ANCHOR_GOOGLE_DRIVE = "google-drive";
export const DELETE_VAULT_GUIDE_ANCHOR_ONEDRIVE = "onedrive";
export const DELETE_VAULT_GUIDE_ANCHOR_DROPBOX = "dropbox";

/**
 * Which of ADR 0002's two delete scopes a vault falls under: `"owned"` for a
 * container Big Bear Backup created for itself, which Delete removes whole,
 * and `"attached"` for a folder or prefix the user pointed the app at, where
 * Delete removes only the objects the format recognises and leaves the
 * container standing.
 *
 * The engine calls the second one `DestroyScope::Recognised`, after the keys
 * it sweeps. This is the vault's word for the same split — ADR 0002's own —
 * because what these sentences describe is the vault, not the key set.
 */
export type VaultDeleteScope = "owned" | "attached";

export interface VaultDeleteProviderNote {
  /** What deleting does to the bill. Static text, never a dollar estimate —
   * this table has no idea how much a vault holds. */
  readonly retention: string;
  /** What deleting does to the data itself: gone for good, or recoverable
   * for a while somewhere only the provider controls. */
  readonly recoverability: string;
  /** The `delete-a-vault-by-hand` guide section that walks through doing
   * this by hand on this provider. */
  readonly guideAnchor: string;
}

/**
 * The one caveat ADR 0002 attaches to the version sweep: "Where a server
 * refuses to list versions, the sweep falls back to by-name deletes and says
 * that versions may remain." Stating the sweep without it would promise
 * unconditionally what the ADR says can fail — on a bucket that hides rather
 * than removes, that is the difference between a clean delete and a bill
 * that never stops.
 */
const EVERY_VERSION_SWEPT =
  "Every stored version is deleted, so nothing is left behind to recover — unless the provider will not list versions, which the app tells you if it happens.";

/**
 * Azure's half of the same sentence, which cannot end the way the S3 rows'
 * does. The sweep still deletes every version by id (ADR 0023), but if soft
 * delete is on for the storage account, Azure keeps each deleted file, and
 * bills for it, until the account's retention period ends: freeing one early
 * needs the permanent-delete permission (`y`) the app's SAS token does not
 * carry, and a container SAS cannot read whether soft delete is on at all. So
 * "nothing is left behind to recover" is exactly what a soft-delete account
 * makes untrue, and the sentence says so once, here, rather than on each tier.
 *
 * The listing caveat stays, for the reason `EVERY_VERSION_SWEPT` gives it, in
 * the one form Azure can produce: the Azure store always lists versions (it
 * cannot read whether the account keeps any, so it never falls back to
 * deleting by name), but a listing can still stop part way, and the app says
 * so when it does.
 */
const AZURE_EVERY_VERSION_SWEPT =
  "Every stored version is deleted — unless Azure stops listing them part way, which the app tells you if it happens. " +
  "But if soft delete is on for the storage account, Azure keeps and bills the deleted files until its retention period ends.";

const NO_BILLING_CONSEQUENCE = "There is no minimum storage duration to consider.";

/**
 * Drive's two halves, which an earlier draft of this row collapsed into one.
 *
 * `files.delete` really is permanent and takes the file's earlier revisions
 * with it, so nothing the app deleted is recoverable — which is what the row
 * used to say, and all it used to say. But every listing in the Drive store
 * pins `trashed = false`, so a file the account holder moved to the bin
 * themselves is invisible to the sweep: not deleted, not counted, and still
 * using the account's storage afterwards. The backend says exactly that with
 * `DeletedObjects::NotListed`, and ADR 0004 records that this sentence had to
 * stop disagreeing with it before any surface reported a Drive destroy.
 *
 * Both halves are here because either alone misleads: "permanent" on its own
 * sends someone away believing an emptied quota they still have to go and
 * empty, and the bin alone would imply the app's own deletes are sitting in
 * it, which they never are.
 *
 * True of an **attached** vault, whose folder is the user's and is still
 * there when the sweep finishes — see `DRIVE_FOLDER_TAKES_THE_BIN_WITH_IT`
 * for the other scope.
 */
const DRIVE_BIN_IS_NOT_SWEPT =
  "What the app deletes goes permanently, earlier revisions included — but anything you moved to Drive's bin yourself is left there, still using your storage, so empty the bin as well.";

/**
 * The same delete on an **owned** vault, where the sweep is not the end of it.
 *
 * An owned-scope Delete follows the sweep with a `DELETE` on the folder the
 * app created — `delete_cloud_folder` in the desktop backend, over
 * `DriveStore::delete_folder`. Google documents that as permanently deleting
 * the folder and every descendant the account owns, and a trashed file keeps
 * its parent — so the descendants it takes include the ones already in the
 * bin. The sweep's blind spot is real and `DeletedObjects::NotListed` still
 * reports it, but the folder delete behind it closes the spot rather than
 * leaving it for the user: by the time they could go and look, there is
 * nothing of this vault's in the bin to empty.
 *
 * Sending them anyway is low harm — a minute spent on an empty bin — but the
 * confirm block renders this sentence directly under `containerScopeSentence`,
 * which is busy telling that same user the whole folder is going. Two
 * sentences about one delete, disagreeing, at the moment they decide (#224).
 *
 * Two things it has to be careful about, both of which an earlier draft got
 * wrong by ending "so there is no bin to empty afterwards".
 *
 * It says what is **lost**, not what is saved. The files this takes are ones
 * the reader put in their own undo, and the folder delete destroys them with
 * no second confirmation and no route back. That is worth stating plainly
 * even though it makes the sentence less reassuring: a reader who trashed
 * something from inside a vault folder to get it out of a restore's way is
 * exactly the reader who needs to know before pressing the button, and
 * "nothing left to empty" tells them the opposite of what is about to happen
 * to it. `gdrive/store.rs`'s own note on trashed objects — the bin is the
 * user's undo and holds files that were never this app's — is the reason to
 * name the loss rather than file it under tidying up.
 *
 * And it stays inside **this vault**. Drive has one bin per account, not one
 * per folder, so a conclusion drawn about "the bin" is read as a claim about
 * everything in it — and most readers have something in it, since every Drive
 * delete lands there for 30 days. Telling them there is nothing to empty is
 * `DRIVE_BIN_IS_NOT_SWEPT`'s own failure in mirror image: away believing a
 * quota is clear when it is not. So the last clause narrows rather than
 * concludes, and `docs/cloud-storage-providers.md`'s step 12 checks exactly
 * that — the delete reaches the folder's descendants, not the account's bin.
 *
 * The sentence is absolute where Google's guarantee is qualified — "every
 * descendant the account **owns**" — and that is deliberate rather than a
 * dropped clause. A descendant of an app-created vault folder that the
 * account does not own is a file somebody shared with them that they then
 * moved in by hand; it survives the folder delete and stays in their bin.
 * Neither half of what this column answers changes for it: Drive bills a
 * file's storage to its owner, so it is not costing this reader anything,
 * and it was not theirs to lose. Hedging for it would buy a vaguer sentence
 * at the one moment the reader is deciding, and buy them nothing to do.
 *
 * It is absolute about the *successful* delete for the same reason, and that
 * is the other clause a reader might look for here. The folder delete runs
 * after the sweep and can fail — `delete_cloud_folder` returns "its contents
 * were deleted, but the folder itself may still be there, so nothing was
 * forgotten" — and the trashed descendants survive with it. But this sentence
 * is rendered in the confirm block, before any of it runs, where every
 * sentence is a description of the delete the user is about to authorise:
 * `containerScopeSentence` sits directly above promising the whole folder
 * goes, on exactly the same condition. A delete that fails there says so in
 * its own words and leaves the vault unforgotten on screen, so nobody is left
 * holding this sentence as a report of what happened. Qualifying one of the
 * two sentences and not the other would re-open, in a second form, the
 * divergence #224 was about.
 */
const DRIVE_FOLDER_TAKES_THE_BIN_WITH_IT =
  "What the app deletes goes permanently, earlier revisions included — and removing the folder takes anything you had moved to Drive's bin from inside it, so those go for good as well. Nothing else in your bin is touched.";

/**
 * Whether the `DeletedObjects::NotListed` shortfall this destination reports is
 * already closed by the time the delete finishes, because the container removal
 * that follows the sweep took what the sweep itself could not see.
 *
 * Google Drive at the owned scope, and nothing else. All three sign-in backends
 * answer `NotListed`, but they are not answering the same question, and the
 * difference is the whole of this predicate:
 *
 * - Drive's reason is the user's **own bin** — "Google Drive keeps anything
 *   moved to the bin, and this app cannot list it". That is data the app never
 *   deleted and cannot see, still parented to the vault's folder. An owned
 *   delete removes that folder, so the shortfall goes with it.
 * - OneDrive's and Dropbox's reasons are **what the app itself deleted**: a
 *   Graph `DELETE` moves the item to the recycle bin, `/files/delete_v2` is a
 *   soft delete. Removing the folder afterwards does not undo either — it is
 *   one more soft delete — so the shortfall stands at both scopes.
 *
 * S3 never removes a container at all (`container_removal` answers
 * `ContainerRemoval::Nothing` for every bucket), so a bucket that would not
 * list versions still holds them whatever the vault's scope.
 *
 * Two surfaces read this, and #224 was those two surfaces disagreeing: the
 * Drive row's recoverability sentence, before the delete, and
 * `VaultDeletedNotice`'s "may still be stored and billed there" paragraph,
 * after it. Neither derives the answer for itself — a sentence and a banner
 * built independently off one fact is the shape that produced the bug, so the
 * fact lives here and both read it.
 *
 * An absent `scope` is attached, for the reason given at `vaultDeleteProviderNote`.
 */
export function unlistedCopiesGoWithTheContainer(
  kind: DestinationKind,
  scope?: VaultDeleteScope,
): boolean {
  return kind === "google-drive" && scope === "owned";
}

/**
 * The provider note for `kind`, and for `"s3"`, for the provider the vault
 * recorded. An absent `storageTier` gets the `"other"` note: an S3-compatible
 * vault always has a provider once the desktop config has loaded (#411), so
 * absence is only ever a caller with no vault, and the generic note is the
 * safe one.
 *
 * `scope` is what ADR 0002's rule decided for this vault, where a caller
 * knows it. **Only the `"google-drive"` row reads it**, and the others are not
 * ignoring it by oversight: Drive is the one destination where the removal
 * that follows the sweep changes what is left behind. Why that is true of
 * Drive and of no other provider is argued once, at
 * `unlistedCopiesGoWithTheContainer` — the predicate this row reads rather
 * than re-testing the scope for itself.
 *
 * Omitting it means "no scope here to read", not "unknown, so hedge": the
 * portal renders this table beside a guide to deleting by hand, where there
 * is no scope to pass. It falls back to the attached sentence, which is the
 * safe direction of the two — an unmentioned bin still costs storage, a bin
 * checked for nothing costs a minute.
 *
 * `storageTier` is the vault's own recorded provider and class (#411), read
 * only by the `"s3"` row. The provider picks the row; for a class the
 * catalogue gives a floor, the class-specific sentence `classRetentionSentence`
 * then replaces `retention`. A class with no floor, or an `"other"` provider
 * the catalogue has no rows for, leaves `retention` as the provider's row says
 * it — the direction argued at the module comment.
 */
export function vaultDeleteProviderNote(
  kind: DestinationKind,
  scope?: VaultDeleteScope,
  storageTier?: VaultStorageTier,
): VaultDeleteProviderNote {
  switch (kind) {
    case "local":
    case "network":
      return {
        retention: NO_BILLING_CONSEQUENCE,
        recoverability: "Files are deleted permanently, not sent to the Recycle Bin.",
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_DRIVE,
      };
    case "google-drive":
      return {
        retention: NO_BILLING_CONSEQUENCE,
        recoverability: unlistedCopiesGoWithTheContainer(kind, scope)
          ? DRIVE_FOLDER_TAKES_THE_BIN_WITH_IT
          : DRIVE_BIN_IS_NOT_SWEPT,
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_GOOGLE_DRIVE,
      };
    case "onedrive":
      return {
        retention: NO_BILLING_CONSEQUENCE,
        recoverability:
          "Whatever the app deletes goes to OneDrive's recycle bin, where it may still count against your quota until the bin is emptied.",
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_ONEDRIVE,
      };
    case "dropbox":
      return {
        retention: NO_BILLING_CONSEQUENCE,
        recoverability:
          "Whatever the app deletes is recoverable in Dropbox for a period before it's gone for good.",
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_DROPBOX,
      };
    case "s3": {
      const note = s3ProviderNote(storageTier?.provider ?? "other");
      const classRetention = storageTier && classRetentionSentence(storageTier);
      return classRetention ? { ...note, retention: classRetention } : note;
    }
  }
}

/**
 * The retention sentence for a vault whose recorded provider and class carry
 * a floor in the catalogue — naming the class under the provider's own name
 * and stating its floor as static text, generalising what the Wasabi row
 * above already said for its one class. `undefined` for a class with no
 * floor, an unknown class, or a provider the catalogue has no rows for
 * (`"other"`): the caller keeps its provider's own sentence in every one of
 * those cases.
 */
function classRetentionSentence(tier: VaultStorageTier): string | undefined {
  const found = lookupStorageClass(tier.provider, tier.storageClass);
  if (found.kind !== "class" || found.storageClass.retentionFloorDays === null) return undefined;

  const { displayName, retentionFloorDays } = found.storageClass;
  return (
    `${vaultProviderName(tier.provider)} bills a ${retentionFloorDays}-day minimum retention per ` +
    `object for ${displayName} — deleting early still bills the remainder of those ${retentionFloorDays} days.`
  );
}

function s3ProviderNote(provider: VaultProvider): VaultDeleteProviderNote {
  switch (provider) {
    case "backblaze":
      return {
        retention: "Backblaze has no minimum retention period, so deleting costs nothing.",
        recoverability: EVERY_VERSION_SWEPT,
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_BACKBLAZE,
      };
    case "wasabi":
      return {
        retention:
          "Wasabi bills a 90-day minimum retention per object — deleting early still bills the remainder of those 90 days.",
        recoverability: EVERY_VERSION_SWEPT,
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_S3_COMPATIBLE,
      };
    case "aws":
    case "gcs":
      return {
        retention:
          "Standard-class objects delete free; other storage classes may bill for early deletion.",
        recoverability: EVERY_VERSION_SWEPT,
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_S3_COMPATIBLE,
      };
    // Its own row, not the AWS and GCS one: the app reaches Azure over its own
    // Blob API (ADR 0023), so neither the S3-compatible section of the guide
    // nor the S3 rows' recoverability sentence describes it. A Cool, Cold or
    // Archive vault gets its tier's floor in place of `retention`, from the
    // catalogue, like any other class with one.
    case "azure":
      return {
        retention: "Hot tier files delete free; the Cool, Cold and Archive tiers bill for early deletion.",
        recoverability: AZURE_EVERY_VERSION_SWEPT,
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_AZURE,
      };
    case "other":
      return {
        retention:
          "This provider may bill a minimum retention period — check its pricing before deleting.",
        recoverability: EVERY_VERSION_SWEPT,
        guideAnchor: DELETE_VAULT_GUIDE_ANCHOR_S3_COMPATIBLE,
      };
  }
}
